import {useState} from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Cookies from "js-cookie";
import { messages } from "../config/messages";

export default function CookiePolicyPage() {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleWithdrawConsent = () => {
    // Remove the analytics cookie
    Cookies.remove("CookieConsent"); // npm install js-cookie
    window.location.reload();
    // Remove Matomo analytics cookies (they usually start with _pk_)
    Object.keys(Cookies.get()).forEach((cookieName) => {
      if (cookieName.startsWith("_pk_")) {
        Cookies.remove(cookieName, { path: "/" });
        setOpen(false);
      }
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          backgroundColor: "grey.50",
        }}
      >
        <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>Cookies policy</Typography>
        <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
          What are cookies
        </Typography>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
          Cookies are small text files stored on your computer or phone when you
          visit a website.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom>
          How we use cookies
        </Typography>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
          This website uses cookies that are set and controlled by us, not by
          external organisations. They are used to:
        </Typography>

        <List sx={{ mb: 3 }}>
          {[
            "store your cookie preferences,",
            "make the website work properly,",
            "collect statistics to help improve our service (Geolocation; operating system, browser, device, duration of your visit, navigation to other pages, pages visited during the same session)",
          ].map((text, i) => (
            <ListItem key={i} sx={{ pl: 0 }}>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={text} />
            </ListItem>
          ))}
        </List>

        <Typography variant="body1" paragraph>
          No personal data is shared with third parties.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom>
          Types of cookies we use
        </Typography>

        <Box>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              mb: 4,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 0,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell><strong>Purpose</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>cookie_consent</TableCell>
                  <TableCell>Cookie settings</TableCell>
                  <TableCell>
                    Stores your cookie preferences so you are not asked again
                  </TableCell>
                  <TableCell>6 months</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>_pk_id#</TableCell>
                  <TableCell>Matomo web analytics</TableCell>
                  <TableCell>
                    Stores a unique visitor ID and helps recognise returning visitors
                  </TableCell>
                  <TableCell>13 months</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>_pk_ses#</TableCell>
                  <TableCell>Matomo web analytics</TableCell>
                  <TableCell>
                    Identifies pages viewed during the same session
                  </TableCell>
                  <TableCell>30 minutes</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Typography variant="body1" paragraph>
          We use <a href="https://matomo.org/" target="_blank" rel="noreferrer"><strong>Matomo</strong></a>, an open-source analytics platform controlled by us.
          It helps us assess how visitors use our website so we can improve its
          content and usability. The analytics cookies collect anonymised
          information, such as:
        </Typography>

        <List sx={{ mb: 1 }}>
          {["masked IP address", "pages visited and time spent", "device, browser, operating system"].map(
            (text, i) => (
              <ListItem key={i} sx={{ pl: 0 }}>
                <ListItemIcon>
                  <InsightsIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItem>
            )
          )}
        </List>

        <Typography variant="body1" paragraph>
          By default, we do not track visitors. You can choose to accept or
          refuse analytics cookies when you first visit the site. You can change
          your choice at any time on this page. All data collected by Matomo stays under our control and is stored on
          servers we manage within the EU. Visitor logs are automatically deleted after 13 months, and only aggregated statistics are kept. We do not use or allow cookies from external parties for advertising or
          social-media tracking.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h4" gutterBottom>
          How to manage cookies
        </Typography>

        <Typography variant="body1" paragraph>
          You can control your cookie choices:
        </Typography>

        <List sx={{ mb: 2 }}>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowRightIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  When you first visit this website, you are asked whether you accept analytics cookies.
                </Typography>
              }
            />
          </ListItem>

          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowRightIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  If you accept, we enable analytics cookies to collect anonymised usage statistics.
                </Typography>
              }
            />
          </ListItem>

          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowRightIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  If you decline, no analytics cookies are set, and your visit is not tracked.
                </Typography>
              }
            />
          </ListItem>

          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowRightIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  You can withdraw your cookies consent by clicking{" "}
                  <Button
                    onClick={handleOpen}
                    variant="text"
                    sx={{ textDecoration: "underline", p: 0, fontSize: "inherit" }}
                  >
                    here
                  </Button>
                  . This will remove the analytics cookies and stop further tracking.
                </Typography>

              }
            />
            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>{messages.matomoWithdrawConcentTooltip}</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  {messages.withdrawPopUpMessage}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{messages.withdrawNo}</Button>
                <Button color="error" onClick={handleWithdrawConsent}>
                  {messages.withdrawYes}
                </Button>
              </DialogActions>
            </Dialog>
          </ListItem>

          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowRightIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  You can also block or delete cookies at any time through your web browser settings.
                </Typography>
              }
            />
          </ListItem>
        </List>

      </Paper>
    </Container>
  );
}
